import { useEffect, useState } from 'react';
import styles from './Header.module.scss';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import * as authService from '../../auth/services/auth.service';
import { useTranslation } from '../../i18n/useTranslation';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Language } from '../models';
import ToggleSwitch from '../toggle-switch/ToggleSwitch';
import { getLanguageCode, getLanguages } from '../services/language.service';
import SearchInput from '../../components/search-input/SearchInput';
import { User } from '../../components/client-panel/components/my-account-info/models/user.model';
import { getCmsLinkToServices } from '../../containers/cms-container/services/cms-navigation.service';
import FixcenterSupportVisitUs from '../../components/fixcenter-support/fixcenter-support-visit-us';
import { useBetween } from 'use-between';
import { ShoppingCartUseBetween } from '../../components/order/use-between/shopping-cart-use-between';
import ShoppingCartInitialize from '../../components/shopping-cart/shopping-cart-initialize';
import {
    BUILDING_SITES_PATHS,
    DOCUMENTS_DELIVERY,
    FAVORITE_PRODUCTS_PATHS,
    PROCUREMENT_LOGIN,
    PRODUCT_FIRST_LEVEL_PATHS,
} from '../constants/breadcrumb.constants';
import logoIcon from '../../assets/icons/logotyp.png';
import creditCard from '../../assets/icons/credit_card.png';
import fileIcon from '../../assets/icons/file_icon.png';
import cartIcon from '../../assets/icons/cart_icon.png';
import StpViewerInitialize from '../../stp-viewer/stp-viewer-initialize';
import { StpViewerUseBetween } from '../../stp-viewer/use-between/stp-viewer-use-between';
import { setStpViewer3dMode } from '../services/local-storage/latest-items-usage.service';
import { redirectToCalaculationProgram } from '../../services/calculation-program.service';
import UserDetailsInitialize from '../initialize/user-details-initialize';
import { UserDetailsUseBetween } from '../use-between/user-details-use-between';
import PermissionsInitialize from '../initialize/permissions-initialize';
import { PermissionsUseBetween } from '../use-between/permissions-use-between';
import { ErpClaimCode } from '../models/erp-claim-code.enum';
import ErrorAlert from '../alerts/error-alert';
import { getCommonErrorTranslate } from '../../configuration/axios-configuration/axios.erros.interceptor.service';
import globalStyles from '../../global-styles.module.scss';
import classNames from 'classnames';
import { claimIsActive } from '../services/claims.service';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import SearchableDropdown from '../dropdown/searchable-dropdown';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';

export default function Header(props: any) {
    const { t, i18n } = useTranslation('translation');
    const navigate = useNavigate();
    const { is3dMode, set3dMode } = useBetween(StpViewerUseBetween);
    const [allLanguages, setAllLanguages] = useState<Language[]>([]);
    const location = useLocation();
    const { userDetails } = useBetween(UserDetailsUseBetween);

    const { quantityShoppingCart } = useBetween(ShoppingCartUseBetween);

    const logOut = (navigate: NavigateFunction) => authService.logOut(navigate);
    const { claims } = useBetween(PermissionsUseBetween);
    const [permissionAlertIsOpen, setOpenPermissionAlert] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    // Only phone-width viewports get the hamburger/drawer; everything wider (including a narrow
    // desktop window) lets the nav row wrap onto additional lines instead of collapsing.
    const isDesktopNav = useMediaQuery('(min-width:600px)');

    useEffect(() => {
        handleSetLanguages();
    }, []);

    const handleRedirectToCalaculationProgram = () => {
        redirectToCalaculationProgram(userDetails as User);
    };

    const toggleViewMode = () => {
        set3dMode(!is3dMode);
        setStpViewer3dMode(!is3dMode);
    };

    const applySelectedClass = (path: string) => {
        const currentPath = location.pathname;
        return path == currentPath ? `${styles.link_div} ${styles.selected_link}` : `${styles.link_div}`;
    };

    const handleSetLanguages = () => {
        getLanguages().then((result) => {
            if (result) setAllLanguages(result?.data as Language[]);
        });
    };

    const checkIfIsLoggedInAndNavigate = (): string => {
        const isLoggedIn: boolean = authService.checkLogInStatus();
        return isLoggedIn ? '/webshop' : '/Home/Product';
    };

    const shouldShowShoppingCart = (): boolean =>
        /webshop|cart/.test(location.pathname.toLowerCase()) ||
        location.pathname.includes(FAVORITE_PRODUCTS_PATHS[0].link) ||
        location.pathname.includes(BUILDING_SITES_PATHS[0].link);

    const changeLanguage = (langCode: string): void => {
        i18n.changeLanguage(langCode);
        localStorage.setItem('lang', langCode);
        const pathname = window.location.pathname;
        const pathWithoutLang = pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
        const newPath = `/${langCode}${pathWithoutLang}`;
        window.location.href = newPath;
    };

    const isPunchoutLogin = window.location.pathname.toLowerCase().includes(PROCUREMENT_LOGIN.PUNCHOUT);

    const isCegelecLogin = window.location.pathname.toLowerCase().includes(PROCUREMENT_LOGIN.CEGELEC);

    const renderNavLinks = (isMobile: boolean) => (
        <Box
            component="nav"
            sx={
                isMobile
                    ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1.5rem' }
                    : {
                          display: 'flex',
                          flexWrap: 'wrap',
                          flexGrow: 1,
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: '1rem',
                      }
            }
        >
            <div
                className={classNames(globalStyles.float_left_item, globalStyles.max_content)}
                style={{ marginBottom: '8px' }}
            >
                {authService.checkLogInStatus() && <SearchInput />}
            </div>
            <div style={{ marginBottom: '8px' }}>
                <SearchableDropdown
                    data={allLanguages}
                    translateCode="languages"
                    defaultValue={getLanguageCode()}
                    propName="code"
                    outlinedLabelName="LANGUAGE"
                    handleChange={changeLanguage}
                ></SearchableDropdown>
            </div>
            <div>
                <div className={classNames(globalStyles.flex_center_item, globalStyles.h_100)} onClick={toggleViewMode}>
                    <ToggleSwitch toggled={is3dMode} />
                    <StpViewerInitialize></StpViewerInitialize>
                </div>
            </div>
            {claimIsActive(claims, ErpClaimCode.CUSTOMER_COMPTA_CLAIM) && (
                <div>
                    <div className={classNames(globalStyles.flex_center_item, globalStyles.h_100)}>
                        <button
                            className={classNames(styles.header_element, globalStyles.default_button_style)}
                            onClick={() => {
                                navigate(DOCUMENTS_DELIVERY[2].link);
                            }}
                        >
                            <span>{t('_INVOICES')}</span>
                        </button>
                    </div>
                </div>
            )}
            {claimIsActive(claims, ErpClaimCode.CUSTOMER_CALC_ROLE) && (
                <div>
                    <div className={classNames(globalStyles.flex_center_item, globalStyles.h_100)}>
                        <button
                            className={classNames(styles.header_element, globalStyles.default_button_style)}
                            onClick={() => {
                                handleRedirectToCalaculationProgram();
                            }}
                        >
                            <span>{t('_CALCULATION_PROGRAM')}</span>
                        </button>
                    </div>
                </div>
            )}

            <div>
                <div className={classNames(globalStyles.flex_center_item, globalStyles.h_100)}>
                    <button
                        className={classNames(styles.header_element, globalStyles.default_button_style)}
                        onClick={() => {
                            navigate(PRODUCT_FIRST_LEVEL_PATHS[1].link);
                        }}
                    >
                        <span>{t('WEB_SHOP')}</span>
                    </button>
                </div>
            </div>
            <div>
                {!isPunchoutLogin && !isCegelecLogin && authService.checkLogInStatus() && userDetails !== null && (
                    <div className={`${styles.header_element} ${styles.column}`}>
                        <UserDetailsInitialize></UserDetailsInitialize>
                        <PermissionsInitialize></PermissionsInitialize>
                        <span>{userDetails?.firstName + ' ' + userDetails?.lastName}</span>
                        <span>{userDetails?.email}</span>
                    </div>
                )}
            </div>
            <div>
                <div className={classNames(globalStyles.flex_center_item, globalStyles.h_100)}>
                    {!authService.checkLogInStatus() && (
                        <button
                            className={classNames(
                                globalStyles.space_between_item,
                                globalStyles.default_button_style,
                                globalStyles.mr_3
                            )}
                            onClick={() => {
                                navigate('/login');
                            }}
                        >
                            <LoginIcon className={globalStyles.mr_1}></LoginIcon>
                            <label className={classNames(globalStyles.cursor_pointer, styles.login_text)}>
                                {t('header.login')}
                            </label>
                        </button>
                    )}
                    {!authService.checkLogInStatus() && (
                        <button
                            className={classNames(globalStyles.space_between_item, globalStyles.default_button_style)}
                            onClick={() => {
                                navigate('/register');
                            }}
                        >
                            <PersonAddIcon className={globalStyles.mr_1}></PersonAddIcon>
                            <label className={classNames(globalStyles.cursor_pointer, styles.register_text)}>
                                {t('header.register')}
                            </label>
                        </button>
                    )}
                    {authService.checkLogInStatus() && (
                        <button
                            className={classNames(globalStyles.space_between_item, globalStyles.default_button_style)}
                            onClick={() => logOut(navigate)}
                        >
                            <LogoutIcon className={globalStyles.mr_1}></LogoutIcon>
                            <label className={classNames(globalStyles.cursor_pointer, styles.logout_text)}>
                                {t('header.logout')}
                            </label>
                        </button>
                    )}
                </div>
            </div>
        </Box>
    );

    return (
        <div className={styles.main_container}>
            <div>
                <AppBar position="static" color="transparent" elevation={0}>
                    <Toolbar
                        disableGutters
                        style={{ marginLeft: 0, marginRight: 0, minWidth: '100%' }}
                        sx={{ flexWrap: 'wrap', paddingLeft: '12px', paddingRight: '12px' }}
                    >
                        <a href="/" style={{ marginLeft: 150 }}>
                            <img
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/');
                                    navigate(0);
                                }}
                                src={logoIcon}
                                alt="Logo"
                            />
                        </a>
                        {isDesktopNav ? (
                            renderNavLinks(false)
                        ) : (
                            <>
                                <IconButton
                                    aria-label="basic-navbar-nav"
                                    onClick={() => setMobileNavOpen(true)}
                                    sx={{ marginLeft: 'auto' }}
                                >
                                    <MenuIcon />
                                </IconButton>
                                <Drawer anchor="right" open={mobileNavOpen} onClose={() => setMobileNavOpen(false)}>
                                    <Box sx={{ width: 320, p: 2 }} onClick={() => setMobileNavOpen(false)}>
                                        {renderNavLinks(true)}
                                    </Box>
                                </Drawer>
                            </>
                        )}
                    </Toolbar>
                </AppBar>
            </div>
            <ErrorAlert
                isOpen={permissionAlertIsOpen}
                handleCloseDialog={(val: boolean) => setOpenPermissionAlert(val)}
                translateCode={getCommonErrorTranslate(ErpClaimCode[ErpClaimCode.CUSTOMER_CALC_ROLE])}
            ></ErrorAlert>
            <div className={styles.bottom_part}>
                <div className={`${styles.links_container}`}>
                    <Link to="/" className={applySelectedClass('/')}>
                        {t('header.home')}
                    </Link>
                    <Link to={checkIfIsLoggedInAndNavigate()} className={applySelectedClass('/products')}>
                        {t('header.products')}
                    </Link>

                    <Link to="/engineering" className={applySelectedClass('/engineering')}>
                        {t('header.ENGINEERING')}
                    </Link>
                    <Link to={getCmsLinkToServices()} className={applySelectedClass('/services')}>
                        {t('header.SERVICES')}
                    </Link>
                    <Link to="/company" className={applySelectedClass('/company')}>
                        {t('header.COMPANY')}
                    </Link>
                    <FixcenterSupportVisitUs />
                    <Link to="/career" className={applySelectedClass('/career')}>
                        {t('header.CAREER')}
                    </Link>
                    <Link to="/contact-us" className={applySelectedClass('/contact-us')}>
                        {t('header.CONTACT US')}
                    </Link>
                    {authService.checkLogInStatus() && shouldShowShoppingCart() && (
                        <div className={`${applySelectedClass('/career')} ${styles.dropdown_container}`}>
                            <span>
                                {t('header.shoppingCart')} ({quantityShoppingCart}) <img src={cartIcon} />▾
                            </span>
                            <div className={styles.dropdown_menu}>
                                <ShoppingCartInitialize></ShoppingCartInitialize>
                                <Link to="/Cart">
                                    {t('header.shoppingCart')} <img src={cartIcon} />
                                </Link>
                                <Link to="/Cart/Order/List">
                                    {t('header.myOrders')}
                                    <img src={creditCard} />
                                </Link>
                                {/* <Link to="/Cart/BackOrder/List">{t("header.myBackOrders")}<img src={creditCard} /></Link> */}
                                <Link to="/Cart/Invoices/List">
                                    {t('header.myInvoices')} <img src={fileIcon} />
                                </Link>
                                <Link to="/Cart/Offers/List">{t('header.myOffers')}</Link>
                                <Link to={FAVORITE_PRODUCTS_PATHS[0].link}>{t('MY_FAVORITE_PRODUCTS')}</Link>
                                <Link to={BUILDING_SITES_PATHS[0].link}>{t('BUILDING_SITES')}</Link>
                                {/* <Link to="/Cart/BuildingSites/List">{t("header.buildingSites")}<img src={homeIcon} /></Link> */}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
